import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../services/api';
import Icon from '../components/Icon';
import { useLocale } from '../utils/locale';

const STATUS_BADGES = {
  active:    { icon: 'badge-active.png',    label: 'Active'    },
  paused:    { icon: 'badge-paused.png',    label: 'Paused'    },
  lead:      { icon: 'badge-lead.png',      label: 'Lead'      },
  completed: { icon: 'badge-completed.png', label: 'Completed' },
};

const METRIC_TYPES = ['weight', 'reps', 'distance', 'time', 'body_measurement', 'custom'];

function OverviewTab({ client }) {
  const fields = [
    ['Age', client.age],
    ['Gender', client.gender],
    ['Height', client.height ? client.height + ' cm' : null],
    ['Weight', client.weight ? client.weight + ' kg' : null],
    ['Body Fat', client.body_fat ? client.body_fat + '%' : null],
    ['Fitness Level', client.fitness_level],
  ].filter(([, v]) => v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {client.goal && (
        <div className="card">
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>Goal</div>
          <div>{client.goal}</div>
        </div>
      )}
      {fields.length > 0 && (
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {fields.map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2 }}>{label}</div>
                <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {client.medical_notes && (
        <div className="card" style={{ borderColor: 'rgba(243,156,18,0.3)', background: 'rgba(243,156,18,0.05)' }}>
          <div style={{ fontSize: 12, color: 'var(--color-warning)', marginBottom: 6 }}>Medical Notes</div>
          <div style={{ fontSize: 14 }}>{client.medical_notes}</div>
        </div>
      )}
      {client.email && <div className="card"><span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>Email </span>{client.email}</div>}
      {client.phone && <div className="card"><span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>Phone </span>{client.phone}</div>}
    </div>
  );
}

function MetricChart({ metric }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get('/sessions/metrics/' + metric.id + '/history').then(data => {
      setHistory(data.map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(d.value),
      })));
    }).catch(() => {});
  }, [metric.id]);

  if (history.length < 2) return null;

  const first = history[0].value;
  const last = history[history.length - 1].value;
  const change = last - first;
  const pct = first !== 0 ? ((change / first) * 100).toFixed(1) : null;

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="icon-chart.png" size={18} />
          <span style={{ fontWeight: 600 }}>{metric.exercise_name}</span>
          {metric.unit && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{metric.unit}</span>}
        </div>
        <div style={{ textAlign: 'right', fontSize: 13 }}>
          <span style={{ color: change >= 0 ? 'var(--color-success)' : 'var(--color-primary)', fontWeight: 600 }}>
            {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}{metric.unit ? ' ' + metric.unit : ''}
          </span>
          {pct && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{change >= 0 ? '+' : ''}{pct}%</div>}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={history} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2E2E2E" />
          <XAxis dataKey="date" tick={{ fill: '#AAAAAA', fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: '#AAAAAA', fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: '#1A1A1A', border: '1px solid #2E2E2E', borderRadius: 8, color: '#fff' }}
            labelStyle={{ color: '#AAAAAA', fontSize: 12 }}
          />
          <Line type="monotone" dataKey="value" stroke="#D72638" strokeWidth={2} dot={{ fill: '#D72638', r: 3 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
        <span>Start: <strong style={{ color: 'var(--color-text)' }}>{first}{metric.unit ? ' ' + metric.unit : ''}</strong></span>
        <span>Current: <strong style={{ color: 'var(--color-text)' }}>{last}{metric.unit ? ' ' + metric.unit : ''}</strong></span>
      </div>
    </div>
  );
}

function MetricsTab({ clientId }) {
  const [metrics,    setMetrics]    = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [panel,      setPanel]      = useState(null); // null | 'custom' | 'template'
  const [form,       setForm]       = useState({ exercise_name: '', metric_type: 'weight', unit: '' });
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/clients/' + clientId + '/metrics'),
      api.get('/metrics'),
    ]).then(([m, t]) => {
      setMetrics(m);
      setTemplates(Array.isArray(t) ? t : []);
    }).finally(() => setLoading(false));
  }, [clientId]);

  async function addCustomMetric(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const m = await api.post('/clients/' + clientId + '/metrics', form);
      setMetrics(prev => [...prev, m]);
      setForm({ exercise_name: '', metric_type: 'weight', unit: '' });
      setPanel(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function addFromTemplate(tmpl) {
    setSaving(true);
    try {
      const m = await api.post('/metrics/' + tmpl.id + '/assign', { client_id: clientId });
      setMetrics(prev => prev.find(x => x.id === m.id) ? prev : [...prev, m]);
      setPanel(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteMetric(id) {
    if (!window.confirm('Delete this metric and all its history?')) return;
    await api.delete('/clients/' + clientId + '/metrics/' + id);
    setMetrics(prev => prev.filter(m => m.id !== id));
  }

  if (loading) return <div className="loading-spinner"><span className="spinner" /></div>;

  // Templates not yet assigned to this client
  const trackedTemplateIds = new Set(metrics.map(m => m.template_id).filter(Boolean));
  const availableTemplates = templates.filter(t => !trackedTemplateIds.has(t.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Action buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button className="btn btn-primary" onClick={() => setPanel(panel === 'template' ? null : 'template')}>
          <Icon name="icon-add.png" size={16} />
          From Template
        </button>
        <button className="btn btn-ghost" onClick={() => setPanel(panel === 'custom' ? null : 'custom')}>
          <Icon name="icon-add.png" size={16} />
          Custom
        </button>
      </div>

      {/* From template panel */}
      {panel === 'template' && (
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Add from Template</div>
          {availableTemplates.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
              No templates available — all templates are already added, or create some in Metrics.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availableTemplates.map(t => (
                <button key={t.id}
                  onClick={() => addFromTemplate(t)}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>{t.exercise_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>
                      {t.metric_type.replace('_', ' ')}{t.unit ? ' · ' + t.unit : ''}
                    </div>
                  </div>
                  <Icon name="icon-add.png" size={18} style={{ opacity: 0.6 }} />
                </button>
              ))}
            </div>
          )}
          <button className="btn btn-ghost" style={{ width: '100%', marginTop: 12 }} onClick={() => setPanel(null)}>Cancel</button>
        </div>
      )}

      {/* Custom metric panel */}
      {panel === 'custom' && (
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Add Custom Metric</div>
          <form onSubmit={addCustomMetric}>
            <div className="form-group">
              <label className="form-label">Metric Name</label>
              <input className="form-input" placeholder="e.g. Bench Press" value={form.exercise_name}
                onChange={(e) => setForm(f => ({ ...f, exercise_name: e.target.value }))} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.metric_type}
                onChange={(e) => setForm(f => ({ ...f, metric_type: e.target.value }))}>
                {METRIC_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Unit (optional)</label>
              <input className="form-input" placeholder="e.g. kg, lbs, km, mins" value={form.unit}
                onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving...' : 'Save Metric'}
              </button>
              <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {metrics.length === 0 ? (
        <div className="empty-state">
          <Icon name="icon-chart.png" size={48} style={{ opacity: 0.3 }} />
          <p>No metrics yet. Add from a template or create a custom one.</p>
        </div>
      ) : (
        <>
          {metrics.map(m => <MetricChart key={m.id} metric={m} />)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {metrics.map(m => (
              <div key={m.id} className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{m.exercise_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>
                    {m.metric_type.replace('_', ' ')}{m.unit ? ' · ' + m.unit : ''}
                    {m.template_id && <span style={{ marginLeft: 6, color: 'var(--color-primary)', fontSize: 11 }}>template</span>}
                  </div>
                </div>
                <button className="icon-btn" onClick={() => deleteMetric(m.id)}>
                  <Icon name="icon-delete.png" alt="Delete" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SessionsTab({ clientId }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/clients/' + clientId + '/sessions').then(setSessions).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="loading-spinner"><span className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <button className="btn btn-primary" onClick={() => navigate('/clients/' + clientId + '/session')}>
        <Icon name="icon-start.png" size={18} />
        Start Session
      </button>
      {sessions.length === 0 ? (
        <div className="empty-state"><p>No sessions logged yet.</p></div>
      ) : (
        sessions.map(s => (
          <div key={s.id} className="card">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            {s.metrics && s.metrics.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {s.metrics.map((m, i) => (
                  <span key={i} style={{ background: 'var(--color-surface-2)', borderRadius: 6, padding: '4px 10px', fontSize: 13 }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>{m.name}: </span>
                    <span style={{ fontWeight: 600 }}>{m.value}</span>
                    {m.unit && <span style={{ color: 'var(--color-text-muted)' }}> {m.unit}</span>}
                  </span>
                ))}
              </div>
            )}
            {s.notes && <div style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-muted)' }}>{s.notes}</div>}
          </div>
        ))
      )}
    </div>
  );
}

function ProgramTab({ clientId }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [clientProgram, setClientProgram] = useState(null);
  const [programDetail, setProgramDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/programs/client/' + clientId).then(async (cp) => {
      setClientProgram(cp);
      if (cp) {
        const detail = await api.get('/programs/' + cp.program_id);
        setProgramDetail(detail);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="loading-spinner"><span className="spinner" /></div>;

  if (!clientProgram) {
    return (
      <div className="empty-state">
        <Icon name="icon-program.png" size={48} style={{ opacity: 0.3 }} />
        <p>No program assigned yet</p>
        <button className="btn btn-primary" style={{ width: 'auto', padding: '0 24px' }} onClick={() => navigate('/programs')}>
          {t('Go to Programs')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>{clientProgram.name}</div>
        {clientProgram.goal && <div style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 8 }}>{clientProgram.goal}</div>}
        <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--color-text-muted)' }}>
          {clientProgram.duration_weeks && <span>{clientProgram.duration_weeks} weeks</span>}
          {clientProgram.sessions_per_week && <span>{clientProgram.sessions_per_week}x / week</span>}
          {clientProgram.start_date && <span>Started {new Date(clientProgram.start_date).toLocaleDateString()}</span>}
        </div>
      </div>
      {programDetail?.sessions?.map(s => (
        <div key={s.id} className="card">
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{s.session_name || 'Day ' + s.day_number}</div>
          {s.exercises.map((ex, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
              <span style={{ fontWeight: 500 }}>{ex.exercise_name}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                {ex.sets && ex.reps ? ex.sets + ' × ' + ex.reps : ex.sets ? ex.sets + ' sets' : ''}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


function NutritionTab({ clientId }) {
  const navigate = useNavigate();
  const [clientPlan, setClientPlan] = useState(null);
  const [planDetail, setPlanDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/nutrition/client/' + clientId).then(async (cp) => {
      setClientPlan(cp);
      if (cp) {
        const detail = await api.get('/nutrition/' + cp.plan_id);
        setPlanDetail(detail);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="loading-spinner"><span className="spinner" /></div>;

  if (!clientPlan) {
    return (
      <div className="empty-state">
        <Icon name="icon-nutrition.png" size={48} style={{ opacity: 0.3 }} />
        <p>No nutrition plan assigned yet</p>
        <button className="btn btn-primary" style={{ width: 'auto', padding: '0 24px' }} onClick={() => navigate('/nutrition')}>
          Go to Nutrition Plans
        </button>
      </div>
    );
  }

  const macroRow = [
    clientPlan.daily_calories  && clientPlan.daily_calories  + ' kcal',
    clientPlan.daily_protein_g && 'P: ' + clientPlan.daily_protein_g + 'g',
    clientPlan.daily_carbs_g   && 'C: ' + clientPlan.daily_carbs_g + 'g',
    clientPlan.daily_fat_g     && 'F: ' + clientPlan.daily_fat_g + 'g',
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>{clientPlan.name}</div>
        {clientPlan.goal && <div style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 8 }}>{clientPlan.goal}</div>}
        {macroRow.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 6 }}>
            {macroRow.map(m => <span key={m}>{m}</span>)}
          </div>
        )}
        {clientPlan.duration_weeks && <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{clientPlan.duration_weeks} weeks</div>}
        {clientPlan.start_date && <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Started {new Date(clientPlan.start_date).toLocaleDateString()}</div>}
      </div>

      {planDetail?.meals?.map(m => (
        <div key={m.id} className="card">
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{m.meal_name}</div>
          {m.foods.map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
              <span style={{ fontWeight: 500 }}>{f.food_name}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                {f.quantity && f.unit ? f.quantity + ' ' + f.unit : ''}
                {f.calories ? ' · ' + f.calories + ' kcal' : ''}
              </span>
            </div>
          ))}
        </div>
      ))}

      {planDetail?.notes && (
        <div className="card" style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>Notes</div>
          {planDetail.notes}
        </div>
      )}
    </div>
  );
}

const TABS = ['Overview', 'Metrics', 'Sessions', 'Program', 'Nutrition', 'Invoices'];

export default function ClientProfile() {
  const { t } = useLocale();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get('tab') || 'Overview');

  useEffect(() => {
    api.get('/clients/' + id).then(setClient).catch(() => navigate('/clients')).finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;
  if (!client) return null;

  const badge = STATUS_BADGES[client.status] || STATUS_BADGES.active;

  return (
    <div>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/clients')}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1, fontSize: 17 }}>{client.first_name} {client.last_name}</h1>
        <button className="icon-btn" onClick={() => navigate('/clients/' + id + '/edit')}>
          <Icon name="icon-edit.png" alt="Edit" />
        </button>
      </div>

      <div style={{ padding: '12px 16px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/icons/icon-user.png" alt=""
          style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', background: 'var(--color-surface-2)' }}
          onError={(e) => e.target.style.display='none'} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{client.first_name} {client.last_name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <img src={'/icons/' + badge.icon} alt={badge.label} style={{ width: 16, height: 16 }} onError={(e) => e.target.style.display='none'} />
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{badge.label}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)', overflowX: 'auto' }}>
        {TABS.map(tabName => (
          <button key={tabName} onClick={() => setTab(tabName)}
            style={{
              flex: '0 0 auto', padding: '12px 16px', background: 'transparent', border: 'none',
              borderBottom: tab === tabName ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: tab === tabName ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
            }}>
            {t(tabName)}
          </button>
        ))}
      </div>

      <div className="page-content">
        {tab === 'Overview'  && <OverviewTab client={client} />}
        {tab === 'Metrics'   && <MetricsTab clientId={id} />}
        {tab === 'Sessions'  && <SessionsTab clientId={id} />}
        {tab === 'Program'   && <ProgramTab clientId={id} />}
        {tab === 'Nutrition'  && <NutritionTab clientId={id} />}
        {tab === 'Invoices'  && <div className="empty-state"><p>Coming in Phase 4</p></div>}
      </div>
    </div>
  );
}
